import { findProductByPartNo, isHardIoCapabilityEligible } from "@automatry/catalog";
import { SpecSnapshotSchema } from "@automatry/schemas";
import { getCatalogProducts } from "./controllerAllocation.js";
import { ENGINE_TIMESTAMP } from "./strategyContext.js";
export function validateApartmentSolution(input) {
    const findings = validateSolutionFindings(input.solution, getCatalogProducts(input.catalog));
    return makeValidationResult({
        id: input.id,
        findings,
        warningRiskCount: input.solution.risks.filter((risk) => risk.severity !== "blocker").length,
        sourceRefs: input.sourceRefs,
        ruleTraceRefs: input.ruleTraceRefs
    });
}
export function validateSnapshot(snapshot, catalog) {
    const products = getCatalogProducts(catalog);
    const findings = snapshot.apartmentSolutions.flatMap((solution) => validateSolutionFindings(solution, products));
    try {
        SpecSnapshotSchema.parse(snapshot);
    }
    catch (error) {
        findings.push({
            code: "schema.invalid_snapshot",
            message: error instanceof Error
                ? `SpecSnapshot does not satisfy the pinned schema: ${error.message}`
                : "SpecSnapshot does not satisfy the pinned schema.",
            severity: "error",
            subjectRef: snapshot.id,
            sourceRefs: snapshot.validation.sourceRefs,
            ruleTraceRefs: snapshot.validation.ruleTraceRefs
        });
    }
    return makeValidationResult({
        id: `validation-${snapshot.id}`,
        findings,
        warningRiskCount: snapshot.apartmentSolutions.flatMap((solution) => solution.risks.filter((risk) => risk.severity !== "blocker")).length,
        sourceRefs: snapshot.validation.sourceRefs,
        ruleTraceRefs: snapshot.validation.ruleTraceRefs
    });
}
export function validateSolutionFindings(solution, products) {
    return [
        ...validateControllerProducts(solution.controllers, products),
        ...validateDeviceProducts(solution.devices, products),
        ...validateInfrastructureProducts(solution.infrastructure, products),
        ...validateLedgers(solution.controllers, solution.pointLedger),
        ...validateTraceCompleteness(solution),
        ...validateBlockingRisks(solution.risks)
    ];
}
function validateDeviceProducts(devices, products) {
    return devices.flatMap((device) => {
        if (!isOverrideTouched(device.ruleTraceRefs)) {
            return [];
        }
        const product = findProductByPartNo(products, device.productPartNo);
        if (!product) {
            return [
                makeFinding({
                    code: "catalog.unknown_device_product",
                    message: `${device.productPartNo} is selected for an override-touched derived device but is absent from the supplied catalog products.`,
                    severity: "error",
                    subjectRef: device.id,
                    sourceRefs: device.sourceRefs,
                    ruleTraceRefs: device.ruleTraceRefs
                })
            ];
        }
        if (!isHardIoCapabilityEligible(product)) {
            return [
                makeFinding({
                    code: "catalog.unreviewed_device_product",
                    message: `${device.productPartNo} is selected for an override-touched derived device but is not reviewed.`,
                    severity: "error",
                    subjectRef: device.id,
                    sourceRefs: device.sourceRefs,
                    ruleTraceRefs: device.ruleTraceRefs
                })
            ];
        }
        return [];
    });
}
function validateControllerProducts(controllers, products) {
    return controllers.flatMap((controller) => {
        const product = findProductByPartNo(products, controller.productPartNo);
        const findings = [];
        if (!product) {
            findings.push(makeFinding({
                code: "catalog.unknown_controller",
                message: `${controller.productPartNo} is not present in the supplied catalog products.`,
                severity: "error",
                subjectRef: controller.id,
                sourceRefs: controller.sourceRefs,
                ruleTraceRefs: controller.ruleTraceRefs
            }));
            return findings;
        }
        if (!isHardIoCapabilityEligible(product)) {
            findings.push(makeFinding({
                code: "catalog.unreviewed_hard_io",
                message: `${controller.productPartNo} is not reviewed for hard IO roles.`,
                severity: "error",
                subjectRef: controller.id,
                sourceRefs: controller.sourceRefs,
                ruleTraceRefs: controller.ruleTraceRefs
            }));
        }
        if (product.capabilities.points.ui !== controller.capacity.ui ||
            product.capabilities.points.di !== controller.capacity.di ||
            product.capabilities.points.ao !== controller.capacity.ao ||
            product.capabilities.points.do !== controller.capacity.do ||
            product.capabilities.points.relay !== controller.capacity.relay) {
            findings.push(makeFinding({
                code: "catalog.capacity_mismatch",
                message: `${controller.productPartNo} controller capacity does not match the supplied catalog product.`,
                severity: "error",
                subjectRef: controller.id,
                sourceRefs: controller.sourceRefs,
                ruleTraceRefs: controller.ruleTraceRefs
            }));
        }
        return findings;
    });
}
function validateInfrastructureProducts(infrastructure, products) {
    return infrastructure.flatMap((item) => {
        if (!item.productPartNo) {
            return [];
        }
        const product = findProductByPartNo(products, item.productPartNo);
        if (!product) {
            return [
                makeFinding({
                    code: "catalog.unknown_infrastructure_product",
                    message: `${item.productPartNo} is selected for infrastructure but is absent from the supplied catalog products.`,
                    severity: "error",
                    subjectRef: item.id,
                    sourceRefs: item.sourceRefs,
                    ruleTraceRefs: item.ruleTraceRefs
                })
            ];
        }
        if (!isHardIoCapabilityEligible(product)) {
            return [
                makeFinding({
                    code: "catalog.unreviewed_infrastructure_product",
                    message: `${item.productPartNo} is selected for infrastructure but is not reviewed for hard roles.`,
                    severity: "error",
                    subjectRef: item.id,
                    sourceRefs: item.sourceRefs,
                    ruleTraceRefs: item.ruleTraceRefs
                })
            ];
        }
        return [];
    });
}
function validateLedgers(controllers, apartmentLedger) {
    const controllerFindings = controllers.flatMap((controller) => controller.pointLedger.violations.map((violation) => makeFinding({
        code: "io.controller_over_capacity",
        message: `${controller.id} is over capacity: ${violation.message}`,
        severity: "error",
        subjectRef: controller.id,
        sourceRefs: controller.sourceRefs,
        ruleTraceRefs: controller.ruleTraceRefs
    })));
    const apartmentFindings = apartmentLedger.violations.map((violation) => makeFinding({
        code: "io.apartment_over_capacity",
        message: `Apartment point ledger is over capacity: ${violation.message}`,
        severity: "error",
        subjectRef: apartmentLedger.id,
        sourceRefs: apartmentLedger.sourceRefs,
        ruleTraceRefs: apartmentLedger.ruleTraceRefs
    }));
    return [...controllerFindings, ...apartmentFindings];
}
function validateTraceCompleteness(solution) {
    const traceables = [
        solution.pointLedger,
        ...solution.logicalZones,
        ...solution.thermalLoads,
        ...solution.pointDemands,
        ...solution.controllers,
        ...solution.devices,
        ...(solution.edgeFeatures ?? []),
        ...solution.infrastructure,
        ...solution.physicalMappings,
        ...solution.mappings,
        ...solution.justifications
    ];
    return traceables.flatMap((item) => {
        const findings = [];
        if (item.sourceRefs.length === 0) {
            findings.push(makeFinding({
                code: "trace.missing_source_ref",
                message: `${item.id} has no sourceRefs.`,
                severity: "error",
                subjectRef: item.id,
                sourceRefs: solution.sourceRefs,
                ruleTraceRefs: solution.ruleTraceRefs
            }));
        }
        if (item.ruleTraceRefs.length === 0) {
            findings.push(makeFinding({
                code: "trace.missing_rule_ref",
                message: `${item.id} has no ruleTraceRefs.`,
                severity: "error",
                subjectRef: item.id,
                sourceRefs: item.sourceRefs.length > 0 ? item.sourceRefs : solution.sourceRefs,
                ruleTraceRefs: solution.ruleTraceRefs
            }));
        }
        if (!item.why || !item.how || ("risk" in item && !item.risk.notes)) {
            findings.push(makeFinding({
                code: "trace.missing_reasoning",
                message: `${item.id} is missing why/how/risk reasoning.`,
                severity: "error",
                subjectRef: item.id,
                sourceRefs: item.sourceRefs.length > 0 ? item.sourceRefs : solution.sourceRefs,
                ruleTraceRefs: item.ruleTraceRefs.length > 0 ? item.ruleTraceRefs : solution.ruleTraceRefs
            }));
        }
        return findings;
    });
}
function isOverrideTouched(ruleTraceRefs) {
    return ruleTraceRefs.some((ruleTraceRef) => ruleTraceRef.startsWith("override-"));
}
function validateBlockingRisks(risks) {
    return risks.flatMap((risk) => risk.severity === "blocker"
        ? [
            makeFinding({
                code: "risk.blocking",
                message: `${risk.id} blocks accepted output: ${risk.title}`,
                severity: "error",
                subjectRef: risk.id,
                sourceRefs: risk.sourceRefs,
                ruleTraceRefs: risk.ruleTraceRefs
            })
        ]
        : []);
}
function makeValidationResult(input) {
    const hasError = input.findings.some((finding) => finding.severity === "error");
    const status = hasError
        ? "rejected"
        : input.warningRiskCount > 0 ||
            input.findings.some((finding) => finding.severity === "warning")
            ? "accepted_with_risks"
            : "accepted";
    return {
        id: input.id,
        status,
        summary: hasError
            ? "Validation rejected the snapshot."
            : status === "accepted_with_risks"
                ? "Validation accepted the snapshot with explicit non-blocking risks."
                : "Validation accepted the snapshot.",
        findings: [...input.findings],
        checkedAt: ENGINE_TIMESTAMP,
        sourceRefs: input.sourceRefs,
        ruleTraceRefs: input.ruleTraceRefs
    };
}
function makeFinding(input) {
    return {
        code: input.code,
        message: input.message,
        severity: input.severity,
        ...(input.subjectRef ? { subjectRef: input.subjectRef } : {}),
        sourceRefs: input.sourceRefs,
        ruleTraceRefs: input.ruleTraceRefs
    };
}
//# sourceMappingURL=validate.js.map